// =============================================================================
// app/src/paginas/ContasPagarDetalhe.tsx — a conta aberta por dentro.
//
// Pedido do dono (25/09/2026): "clicar na linha e entender o descritivo de
// cada item — o que é, quanto é e onde foi pago — com o comprovante da
// cobrança quando abrir". E, na volta: "junta tudo no mesmo bloco, painel e
// código de barras/botões de anexo".
//
// Por isso aqui é UM bloco só, com as seções separadas por fio, na ordem em
// que as perguntas ocorrem a quem está olhando a conta:
//
//   1. O QUE É       — descrição, categoria, fixa ou avulsa, a observação.
//   2. QUANTO É      — previsto × real, e a diferença quando os dois existem.
//   3. ONDE FOI PAGO — data, forma, quem deu a baixa, e se saiu do caixa.
//   4. O QUE O PAPEL DIZ — o código de barras carrega valor e vencimento
//      (077 + lib/codigoBarras); comparar com o que está digitado achou quatro
//      contas erradas na conferência de 18/09, então a conferência aparece
//      sozinha, toda vez.
//   5. O CÓDIGO      — a linha digitável, para copiar e mandar (vem de fora,
//      por `children`: o painel é do ContasPagar, que tem as mutações).
//   6. OS DOCUMENTOS — a folha desenhada AQUI, e as ações da vaga (trocar,
//      tirar, trocar de lugar) junto do documento a que pertencem, em vez de
//      numa lista à parte.
//
// O BOLETO CARREGA SOZINHO ao abrir a linha; o comprovante só a pedido. É o
// que o dono pediu ("o comprovante da cobrança quando abrir") e também o que
// poupa 4G: são ~500 KB por folha, e o recibo é o que menos se olha.
//
// A prévia é `data:` (lib/anexoPrevia): a CSP recusa `blob:` em <img>.
// =============================================================================
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ComprovanteMeta, ContaPagar, TipoDeAnexo } from '@core/types';
import { obterComprovanteConta } from '../lib/consultas';
import { abrirComprovante } from '../lib/comprovante';
import { previaDoAnexo } from '../lib/anexoPrevia';
import { dadosDoCodigo } from '../lib/codigoBarras';
import { brl, hojeISO } from '../lib/formato';
import { ROTULO_DA_FORMA } from '../lib/rotulos';
import { mensagemDeErro } from '../lib/supabase';
import { rotulo } from './ContasPagarFormularios';

const dataBR = (iso: string) => iso.split('-').reverse().join('/');

const valorDa = (c: ContaPagar) =>
  (c.valor_real ?? c.valor_previsto) === null ? null : Number(c.valor_real ?? c.valor_previsto);

/** "boleto-luz.pdf · 320 KB" — o nome que o dono deu, senão o formato. */
export const descreverAnexo = (m: ComprovanteMeta) =>
  `${m.nome ?? (m.content_type === 'application/pdf' ? 'PDF' : 'foto')} · ${Math.max(1, Math.round(m.bytes / 1024))} KB`;

/** As escritas das vagas de anexo moram no ContasPagar (é lá que está o input
 *  de arquivo e as mutações); o detalhe só as chama. */
export interface AcoesDoAnexo {
  anexar: (tipo: TipoDeAnexo) => void;
  tirar: (tipo: TipoDeAnexo) => void;
  mudarVaga: (tipo: TipoDeAnexo) => void;
  /** Alguma escrita em curso que deve travar os botões desta vaga. */
  ocupada: (tipo: TipoDeAnexo) => boolean;
  /** ESTA vaga está recebendo arquivo agora — muda o rótulo, só. */
  subindo: (tipo: TipoDeAnexo) => boolean;
}

function Bloco({ titulo, children }: { titulo?: string; children: ReactNode }) {
  return (
    <div className="space-y-0.5 py-3">
      {titulo && <p className="rotulo">{titulo}</p>}
      {children}
    </div>
  );
}

/** O que o papel diz × o que está digitado. Só aparece com código de barras,
 *  e só grita quando DIVERGE — concordar é o normal. */
function OQueOBoletoDiz({ conta }: { conta: ContaPagar }) {
  if (!conta.codigo_barras) return null;
  const lido = dadosDoCodigo(conta.codigo_barras, hojeISO());
  const valor = valorDa(conta);
  const valorBate = lido.valor === null || valor === null || Math.abs(lido.valor - valor) < 0.005;
  const dataBate = lido.vencimento === null || lido.vencimento === conta.vencimento;

  return (
    <Bloco titulo="O que o código de barras diz">
      <p className="text-sm">
        {lido.quem}
        {lido.valor !== null && <> · <span className="numero">{brl(lido.valor)}</span></>}
        {lido.vencimento && <> · vence {dataBR(lido.vencimento)}</>}
      </p>
      {valorBate && dataBate ? (
        <p className="flex items-center gap-2 text-xs text-mute">
          <span className="ponto text-ok" aria-hidden="true" />
          Bate com o que está lançado.
        </p>
      ) : (
        <p className="text-xs text-alerta">
          {!valorBate && valor !== null && lido.valor !== null && (
            <>O papel diz {brl(lido.valor)} e a conta está com {brl(valor)}. </>
          )}
          {!dataBate && lido.vencimento && (
            <>O papel vence {dataBR(lido.vencimento)} e a conta está em {dataBR(conta.vencimento)}. </>
          )}
          Confira antes de pagar.
        </p>
      )}
    </Bloco>
  );
}

/** Uma vaga de anexo: a folha desenhada e as ações dela, juntas.
 *
 *  Prévia nula NÃO é erro: é "este PDF não é uma foto de folha" (um PDF de
 *  banco, com texto). Aí o caminho é a outra aba, que serve para qualquer um. */
/** "boleto" / "comprovante" — o nome que o dono usa, para o botão dizer o que
 *  vai sumir em vez de um "tirar" solto. */
const rotuloDoTipo = (t: TipoDeAnexo) => (t === 'boleto' ? 'boleto' : 'comprovante');

function Documento({ conta, tipo, titulo, acoes }: {
  conta: ContaPagar; tipo: TipoDeAnexo; titulo: string; acoes: AcoesDoAnexo;
}) {
  const meta = conta[tipo];
  // O boleto abre junto com a linha; o recibo espera um toque (4G).
  const [pedido, setPedido] = useState(tipo === 'boleto');
  // Apagar documento é de mão única: o arquivo não fica em lugar nenhum depois.
  const [confirmando, setConfirmando] = useState<TipoDeAnexo | null>(null);

  const anexo = useQuery({
    queryKey: ['anexo-da-conta', conta.id, tipo],
    queryFn: () => obterComprovanteConta(conta.id, tipo),
    enabled: pedido && !!meta,
    staleTime: 5 * 60_000,
  });

  const outra: TipoDeAnexo = tipo === 'boleto' ? 'comprovante' : 'boleto';

  if (!meta) {
    return (
      <Bloco titulo={titulo}>
        <p className="text-xs text-dim">
          Nenhum ainda —{' '}
          <button type="button" className="text-signal-soft underline hover:text-paper" disabled={acoes.ocupada(tipo)}
                  onClick={() => acoes.anexar(tipo)}>
            {acoes.subindo(tipo) ? 'subindo…' : 'anexar'}
          </button>
        </p>
      </Bloco>
    );
  }

  const previa = anexo.data ? previaDoAnexo(anexo.data.dados_base64, anexo.data.content_type) : null;
  const abrirInteiro = () =>
    anexo.data && abrirComprovante(anexo.data.dados_base64, anexo.data.content_type);

  return (
    <Bloco titulo={titulo}>
      {!pedido && (
        <button type="button" className="botao-secundario px-3 py-1 text-xs"
                onClick={() => setPedido(true)}>
          Mostrar o {titulo.toLowerCase()} ({Math.max(1, Math.round(meta.bytes / 1024))} KB)
        </button>
      )}
      {pedido && anexo.isPending && <p className="text-xs text-dim">Carregando a folha…</p>}
      {pedido && anexo.error && <p className="text-xs text-erro">{mensagemDeErro(anexo.error)}</p>}
      {anexo.data && previa && (
        <button type="button" onClick={abrirInteiro} title="Abrir inteiro"
                className="block w-full cursor-zoom-in border border-edge bg-paper p-1">
          <img src={previa} alt={`${titulo} de ${conta.descricao}`}
               className="max-h-[32rem] w-full object-contain" />
        </button>
      )}
      {anexo.data && !previa && (
        <p className="text-xs text-dim">Este PDF não é uma foto de folha.</p>
      )}
      {/* As ações da VAGA, junto do documento — antes viviam numa lista à
          parte, que repetia os mesmos dois nomes logo abaixo. */}
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-xs">
        <span className="min-w-0 truncate text-dim">{descreverAnexo(meta)}</span>
        {anexo.data && (
          <button type="button" className="text-signal-soft underline hover:text-paper" onClick={abrirInteiro}>
            abrir inteiro
          </button>
        )}
        <button type="button" className="text-dim underline" disabled={acoes.ocupada(tipo)}
                onClick={() => acoes.anexar(tipo)}>
          {acoes.subindo(tipo) ? 'subindo…' : 'trocar'}
        </button>
        {/* TIRAR É DESTRUTIVO E PARECIA NÃO SER. Ele era o terceiro de quatro
            links cinzas idênticos, e o dono relatou em 28/09/2026 que "não tem
            como remover" — a capacidade existia (conferida no banco: remover e
            reinserir funcionam nas duas vagas), o que faltava era ela ser
            achável e parecer o que é. Agora tem a cor do perigo, diz o NOME do
            que vai sumir, e pede confirmação como o desfazer da baixa já
            pedia. */}
        {confirmando === tipo ? (
          <>
            <span className="text-erro">apagar o {rotuloDoTipo(tipo)}?</span>
            <button type="button" className="font-bold text-erro underline"
                    disabled={acoes.ocupada(tipo)}
                    onClick={() => { setConfirmando(null); acoes.tirar(tipo); }}>
              sim, apagar
            </button>
            <button type="button" className="text-dim underline"
                    onClick={() => setConfirmando(null)}>não</button>
          </>
        ) : (
          <button type="button" className="text-erro underline" disabled={acoes.ocupada(tipo)}
                  onClick={() => setConfirmando(tipo)}>
            tirar o {rotuloDoTipo(tipo)}
          </button>
        )}
        {/* O que entrou na vaga errada — todo anexo de antes da 080 é
            "comprovante" — muda de lugar sem baixar e subir de novo. */}
        <button type="button" className="text-dim underline" disabled={acoes.ocupada(tipo)}
                onClick={() => acoes.mudarVaga(tipo)}>
          {conta[outra] ? 'trocar de lugar' : tipo === 'boleto' ? 'é comprovante' : 'é boleto'}
        </button>
      </p>
    </Bloco>
  );
}

export function DetalheDaConta({ conta, acoes, children }: {
  conta: ContaPagar;
  acoes: AcoesDoAnexo;
  /** O painel do código de barras, que mora no ContasPagar: entra aqui dentro
   *  como mais uma seção, para a conta aberta ser UM bloco só. */
  children?: ReactNode;
}) {
  const valor = valorDa(conta);
  const estimada = conta.valor_real === null;
  const atrasada = !conta.pago_em && conta.vencimento < hojeISO();

  return (
    <div className="mt-2 w-full divide-y divide-edge border border-edge bg-panel-alt px-3">
      <Bloco titulo="O que é">
        <p className="text-sm font-bold">{conta.descricao}</p>
        <p className="text-xs text-mute">
          {rotulo(conta.categoria)}
          {conta.recorrencia_id ? ' · conta fixa, criada pela regra do mês' : ' · conta avulsa'}
          {conta.supplier_order_id ? ' · parcela de um pedido ao fornecedor' : ''}
        </p>
        {conta.observacao && <p className="text-xs text-dim">{conta.observacao}</p>}
      </Bloco>

      <Bloco titulo="Quanto é">
        {valor === null ? (
          <p className="text-sm">
            <span className="text-mute">A definir</span> — conta de valor variável, esperando a fatura.
          </p>
        ) : (
          <>
            <p className={`numero text-lg ${estimada ? 'text-mute' : 'text-paper'}`}>
              {estimada ? '~' : ''}{brl(valor)}
              {estimada && <span className="ml-2 font-sans text-xs font-normal text-mute">estimativa</span>}
            </p>
            {conta.valor_real !== null && conta.valor_previsto !== null
              && Number(conta.valor_real) !== Number(conta.valor_previsto) && (
              <p className="text-xs text-mute">
                Estava previsto {brl(Number(conta.valor_previsto))} — diferença de{' '}
                {brl(Number(conta.valor_real) - Number(conta.valor_previsto))}.
              </p>
            )}
          </>
        )}
      </Bloco>

      <Bloco titulo={conta.pago_em ? 'Onde foi pago' : 'Ainda não foi paga'}>
        {conta.pago_em ? (
          <>
            <p className="text-sm">
              {dataBR(conta.pago_em)}
              {conta.forma_pagamento && ` · ${ROTULO_DA_FORMA[conta.forma_pagamento]}`}
              {conta.pago_por_nome && ` · baixada por ${conta.pago_por_nome}`}
            </p>
            <p className="text-xs text-dim">
              {conta.cash_movement_id
                ? 'A saída está lançada no caixa do dia.'
                : 'Sem lançamento no caixa — esta baixa é anterior ao vínculo com o livro.'}
            </p>
          </>
        ) : (
          <p className="flex flex-wrap items-center gap-2 text-sm">
            {atrasada && <span className="selo selo-atrasado">atrasada</span>}
            <span>
              Vence em <span className="numero">{dataBR(conta.vencimento)}</span>
              {atrasada ? ' — está atrasada.' : '.'}
            </span>
          </p>
        )}
      </Bloco>

      <OQueOBoletoDiz conta={conta} />
      {children && <div className="py-3">{children}</div>}
      <Documento conta={conta} tipo="boleto" titulo="Boleto" acoes={acoes} />
      <Documento conta={conta} tipo="comprovante" titulo="Comprovante" acoes={acoes} />
      <Bloco>
        <p className="text-xs text-dim">
          Boleto é o que cobra — o boleto, a conta de luz, a guia. Comprovante é o recibo do pagamento.
        </p>
      </Bloco>
    </div>
  );
}
